/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { UserList } from '../models/user-list';
import { UserSearchOptions } from '../models/user-search-options';
import { TerminalSummaryList } from '../models/terminal-summary-list';
import { CarrierSummaryList } from '../models/carrier-summary-list';
import { TankSummaryList } from '../models/tank-summary-list';
import { User } from '../models/user';

/**
 * All about the users
 */
@Injectable({
  providedIn: 'root',
})
class UsersService extends __BaseService {
  static readonly searchUsersPath = '/users/search';
  static readonly retrieveUserTerminalsPath = '/users/{uid}/terminals';
  static readonly retrieveUserCarriersPath = '/users/{uid}/carriers';
  static readonly retrieveUserTanksPath = '/users/{uid}/tanks';
  static readonly retrieveUserPath = '/users/{uid}';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Search users, with pagination
   *
   * Search users, with pagination
   * @param params The `UsersService.SearchUsersParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * DATE : the registration date
   *     * LAST_NAME : the last name
   *     * FIRST_NAME : the first name
   *     * EMAIL : the email
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The user search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  searchUsersResponse(params: UsersService.SearchUsersParams): __Observable<__StrictHttpResponse<UserList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.sortFieldName != null) __params = __params.set('sortFieldName', params.sortFieldName.toString());
    if (params.sortDirection != null) __params = __params.set('sortDirection', params.sortDirection.toString());
    __body = params.search;
    if (params.offset != null) __params = __params.set('offset', params.offset.toString());
    if (params.limit != null) __params = __params.set('limit', params.limit.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/users/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<UserList>;
      })
    );
  }
  /**
   * Search users, with pagination
   *
   * Search users, with pagination
   * @param params The `UsersService.SearchUsersParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * DATE : the registration date
   *     * LAST_NAME : the last name
   *     * FIRST_NAME : the first name
   *     * EMAIL : the email
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The user search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  searchUsers(params: UsersService.SearchUsersParams): __Observable<UserList> {
    return this.searchUsersResponse(params).pipe(
      __map(_r => _r.body as UserList)
    );
  }

  /**
   * Retrieve the terminals accessible by a user
   *
   * Retrieve the terminals accessible by a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserTerminalsResponse(uid: string): __Observable<__StrictHttpResponse<TerminalSummaryList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/users/${encodeURIComponent(String(uid))}/terminals`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TerminalSummaryList>;
      })
    );
  }
  /**
   * Retrieve the terminals accessible by a user
   *
   * Retrieve the terminals accessible by a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserTerminals(uid: string): __Observable<TerminalSummaryList> {
    return this.retrieveUserTerminalsResponse(uid).pipe(
      __map(_r => _r.body as TerminalSummaryList)
    );
  }

  /**
   * Retrieve the carriers accessible by a user
   *
   * Retrieve the carriers accessible by a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserCarriersResponse(uid: string): __Observable<__StrictHttpResponse<CarrierSummaryList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/users/${encodeURIComponent(String(uid))}/carriers`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<CarrierSummaryList>;
      })
    );
  }
  /**
   * Retrieve the carriers accessible by a user
   *
   * Retrieve the carriers accessible by a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserCarriers(uid: string): __Observable<CarrierSummaryList> {
    return this.retrieveUserCarriersResponse(uid).pipe(
      __map(_r => _r.body as CarrierSummaryList)
    );
  }

  /**
   * Retrieve the tanks accessible by a user
   *
   * Retrieve the tanks accessible by a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserTanksResponse(uid: string): __Observable<__StrictHttpResponse<TankSummaryList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/users/${encodeURIComponent(String(uid))}/tanks`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TankSummaryList>;
      })
    );
  }
  /**
   * Retrieve the tanks accessible by a user
   *
   * Retrieve the tanks accessible by a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserTanks(uid: string): __Observable<TankSummaryList> {
    return this.retrieveUserTanksResponse(uid).pipe(
      __map(_r => _r.body as TankSummaryList)
    );
  }

  /**
   * Retrieve a user
   *
   * Retrieve a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUserResponse(uid: string): __Observable<__StrictHttpResponse<User>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/users/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<User>;
      })
    );
  }
  /**
   * Retrieve a user
   *
   * Retrieve a user
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveUser(uid: string): __Observable<User> {
    return this.retrieveUserResponse(uid).pipe(
      __map(_r => _r.body as User)
    );
  }
}

namespace UsersService {

  /**
   * Parameters for searchUsers
   */
  export interface SearchUsersParams {

    /**
     * Indicates the field to use for sorting the data:
     *   * DATE : the registration date
     *   * LAST_NAME : the last name
     *   * FIRST_NAME : the first name
     *   * EMAIL : the email
     */
    sortFieldName?: 'DATE' | 'LAST_NAME' | 'FIRST_NAME' | 'EMAIL';

    /**
     * Sort direction : ASC, DESC
     */
    sortDirection?: 'ASC' | 'DESC';

    /**
     * The user search options object
     */
    search?: UserSearchOptions;

    /**
     * The list items offset
     */
    offset?: number;

    /**
     * The list items limit
     */
    limit?: number;
  }
}

export { UsersService }
